import React from 'react';
import { Card } from '@/components/ui/card';
import { Chart } from '../engine/chart';
import { signOf } from '../engine/aspects';
import { SIGN_MEANINGS, BIG_THREE_FRAMING, BIG_THREE_BODY_COPY } from '../data/constants';
import { Sparkles } from 'lucide-react';
import { getRising, getSunInSign, getMoonInSign } from '../lib/contentLookup';

interface Props { chart: Chart }

export const BigThreeCard: React.FC<Props> = ({ chart }) => {
  const sun = chart.placements.Sun;
  const moon = chart.placements.Moon;
  const ascLong = chart.asc;
  const unknownTime = !!chart.unknownTime;

  type Key = 'Sun' | 'Moon' | 'Rising';
  const items = [
    sun ? { label: 'Sun', long: sun.longitude, house: sun.house, key: 'Sun' as Key } : null,
    moon ? { label: 'Moon', long: moon.longitude, house: moon.house, key: 'Moon' as Key } : null,
    unknownTime
      ? null
      : { label: 'Rising', long: ascLong, house: 1, key: 'Rising' as Key },
  ].filter(Boolean) as { label: string; long: number; house: number | null; key: Key }[];

  // New Moon = Sun & Moon within ~12° (orb of conjunction with margin).
  let isNewMoon = false;
  if (sun && moon) {
    let diff = Math.abs(sun.longitude - moon.longitude);
    if (diff > 180) diff = 360 - diff;
    isNewMoon = diff <= 12;
  }

  return (
    <div className="space-y-4">
      {unknownTime && (
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
          <Sparkles className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
          <p className="text-muted-foreground">
            <span className="font-medium text-foreground">Birth time unknown.</span>{' '}
            Rising sign, houses, and Midheaven are not shown — they require an exact birth time.
          </p>
        </div>
      )}
      {isNewMoon && (
        <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
          <Sparkles className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
          <div>
            <span className="font-medium text-foreground">New Moon birth.</span>{' '}
            <span className="text-muted-foreground">
              Your Sun and Moon are conjunct — your identity and emotional nature are
              fused. You can be intensely self-contained, with little gap between who
              you are and how you feel.
            </span>
          </div>
        </div>
      )}
      <div className={`grid gap-4 ${unknownTime ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
        {items.map((it) => {
          const s = signOf(it.long);
          const meaning = SIGN_MEANINGS[s.sign];
          const framing = BIG_THREE_FRAMING[it.key];
          const fallback = BIG_THREE_BODY_COPY[it.key]?.[s.sign as keyof typeof BIG_THREE_BODY_COPY['Sun']];

          // Rich prose from Phase 3 libraries.
          const sunEntry = it.key === 'Sun' ? getSunInSign(s.sign) : null;
          const moonEntry = it.key === 'Moon' ? getMoonInSign(s.sign) : null;
          const risingEntry = it.key === 'Rising' ? getRising(s.sign) : null;
          const richParagraph =
            sunEntry?.paragraph ?? moonEntry?.paragraph ?? risingEntry?.paragraph ?? null;
          const shadow =
            sunEntry?.shadowSide ?? moonEntry?.shadowSide ?? risingEntry?.shadowSide ?? null;
          const bodyAndManner = risingEntry?.bodyAndManner ?? null;

          // Rising gets ~30% more space when present (full row on md, normal on lg).
          const isRising = it.key === 'Rising';
          const cardSpan = isRising && !unknownTime ? 'md:col-span-2 lg:col-span-1' : '';

          return (
            <Card key={it.label} className={`p-5 space-y-3 bg-card/50 ${cardSpan}`}>
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-lg">{it.label}</h3>
                <span className="text-2xl">{s.glyph}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {s.degree.toFixed(1)}° {s.sign}
                {it.key !== 'Rising' && it.house ? ` · House ${it.house}` : ''}
              </p>
              <p className="text-xs text-muted-foreground">{framing.lens}</p>
              {richParagraph ? (
                <div className="space-y-3">
                  <p className="text-sm text-foreground/85 leading-relaxed">{richParagraph}</p>
                  {bodyAndManner && (
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">Body &amp; manner</p>
                      <p className="text-sm text-foreground/85 leading-relaxed">{bodyAndManner}</p>
                    </div>
                  )}
                  {shadow && (
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">Shadow side</p>
                      <p className="text-sm text-foreground/85 leading-relaxed">{shadow}</p>
                    </div>
                  )}
                </div>
              ) : fallback ? (
                <p className="text-sm text-foreground/85 leading-relaxed">{fallback}</p>
              ) : meaning ? (
                <p className="text-xs italic text-foreground/80">
                  {framing.verb} <span className="not-italic font-medium text-foreground">{meaning.keyword}</span> — {meaning.traits}.
                </p>
              ) : null}
            </Card>
          );
        })}
      </div>
    </div>
  );
};